/**
 * Shared chrome for SignalBridge pages. Reuses Uni* / Waba primitives
 * and always shows stage-by-stage help via UniPageShell.
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { getJSON, postJSON } from '../../api/client.js';
import { FieldLabel, copyToClipboard } from '../uni/UniShared.jsx';
import { UniActionButton, UniPageShell, useUniList } from '../waba/WabaShared.jsx';
import {
  DEFAULT_DISPLAY_TIMEZONE,
  TZ_EVENT,
  browserTimeZone,
  commonTimeZones,
  formatInTimeZone,
  parseUtcDate,
  useOrgTimeZone,
} from '../../lib/displayTimezone.js';
import './signalbridge.css';

export const SB_API = '/api/signalbridge';
export const THEME = 'signalbridge';
export const HOME = '/signalbridge';

export {
  browserTimeZone,
  commonTimeZones,
  formatInTimeZone,
  parseUtcDate,
};

const FALLBACK_TZ = DEFAULT_DISPLAY_TIMEZONE;

export function readStoredTimeZone() {
  return '';
}

export function writeStoredTimeZone(tz) {
  try {
    window.dispatchEvent(new CustomEvent(TZ_EVENT, { detail: tz }));
  } catch {
    /* ignore */
  }
}

export function useDisplayTimezone() {
  return useOrgTimeZone() || FALLBACK_TZ;
}

export function CopyField({ label, value, compact }) {
  const [ok, setOk] = React.useState(false);
  if (!value) return null;
  const copy = async () => {
    const copied = await copyToClipboard(String(value));
    if (copied) {
      setOk(true);
      window.setTimeout(() => setOk(false), 1600);
    }
  };
  return (
    <div className="sb-copy">
      {label ? <span className="sb-copy__label">{label}</span> : null}
      <span className="sb-copy__row">
        <code className="sb-copy__value" title={String(value)}>{value}</code>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={copy}>
          {ok ? 'Copied' : (compact ? 'Copy' : 'Copy')}
        </button>
      </span>
    </div>
  );
}

export function TechDetails({ title = 'Technical details', data }) {
  if (data == null || data === '') return null;
  const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  if (!text || text === '{}' || text === 'null') return null;
  return (
    <details className="sb-tech">
      <summary>{title}</summary>
      <pre>{text}</pre>
    </details>
  );
}

export function EasyField({ label, hint, help, children }) {
  const autoId = React.useId();
  const inputId = (React.isValidElement(children) && children.props.id) || autoId;
  const child = React.isValidElement(children)
    ? React.cloneElement(children, { id: inputId })
    : children;
  return (
    <div className="sb-field">
      <FieldLabel htmlFor={inputId} label={label} help={help} />
      {hint ? <span className="sb-field__hint">{hint}</span> : null}
      {child}
    </div>
  );
}

export const ORDER_SYNC_HOUR_OPTIONS = [
  { value: 48, label: 'Last 48 hours' },
  { value: 168, label: 'Last 7 days' },
  { value: 720, label: 'Last 30 days' },
  { value: 1440, label: 'Last 60 days (Shopify max without extra scope)' },
];

export const DEFAULT_MANUAL_ORDER_SYNC_HOURS = 168;

export async function runStoreOrderSync(storeId, hours, { onProgress } = {}) {
  const notify = (data) => {
    if (typeof onProgress === 'function' && data) onProgress(data);
  };
  const start = await postJSON(`${SB_API}/stores/${storeId}/sync-orders/`, { hours });
  notify(start);
  if (!start || start.started !== true) return start;
  const job = start.job_id;
  if (!job) return start;
  const deadline = Date.now() + 12 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    let data;
    try {
      data = await getJSON(
        `${SB_API}/stores/${storeId}/sync-orders/?job=${encodeURIComponent(job)}`,
      );
    } catch (err) {
      const status = err && err.status;
      if (status === 404) {
        return {
          ok: false,
          status: 'error',
          message: 'The sync job expired. Click Sync again.',
        };
      }
      throw err;
    }
    notify(data);
    if (data && data.started === false) return data;
    if (data && (data.status === 'done' || data.status === 'error')) return data;
  }
  return {
    ok: true,
    started: true,
    status: 'running',
    job_id: job,
    message: (
      (start && start.message)
      || 'Order sync is still running in the background. Refresh Event log in a minute.'
    ),
  };
}

export function orderSyncOutcome(data) {
  if (!data) return { failed: false, running: false };
  const failed = data.ok === false || Boolean(data.error);
  const running = !failed && (data.started === true || data.status === 'running');
  return { failed, running };
}

export function OrderSyncStatus({ data }) {
  if (!data) return null;
  const { failed, running } = orderSyncOutcome(data);
  const cls = failed ? 'ty-error' : (running ? 'uni-note' : 'ty-ok');
  const icon = failed ? '⚠️' : (running ? '…' : '✓');
  const text = String(
    data.message
    || data.detail
    || data.error
    || (running ? 'Order sync is running…' : 'Order sync finished.'),
  );
  return (
    <>
      <div className={cls} role="status" style={{ marginTop: 12 }}>
        <div className="ty-error__head">
          <span className="ty-error__icon" aria-hidden="true">{icon}</span>
          {text}
        </div>
      </div>
      <TechDetails title="Order sync" data={data} />
    </>
  );
}

export function OrderSyncHoursField({ value, onChange }) {
  return (
    <EasyField
      label="Order lookback"
      hint="48 hours is the automatic gap-fill, not Shopify’s limit. Pick 7–60 days to import older orders into the Event log. Meta only accepts the last 7 days of purchases; older rows are stored as skipped-too-old."
    >
      <select
        value={String(value)}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {ORDER_SYNC_HOUR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </EasyField>
  );
}

export function storeCanSyncOrders(store) {
  if (!store || store.is_active === false) return false;
  if (store.platform === 'SHOPIFY') return Boolean(store.api_access_token_set);
  if (store.platform === 'GOKWIK') {
    return Boolean(store.gokwik_app_id_set && store.api_access_token_set);
  }
  return false;
}

export function StoreOrderSyncPanel({ onSynced }) {
  const stores = useUniList(`${SB_API}/stores/?page_size=200`);
  const rows = stores.rows || [];
  const syncable = rows.filter(storeCanSyncOrders);
  const firstId = syncable[0] ? String(syncable[0].id) : '';
  const [storeId, setStoreId] = React.useState('');
  const [hours, setHours] = React.useState(DEFAULT_MANUAL_ORDER_SYNC_HOURS);
  const [busy, setBusy] = React.useState(false);
  const [syncOut, setSyncOut] = React.useState(null);

  React.useEffect(() => {
    if (!storeId && firstId) setStoreId(firstId);
  }, [storeId, firstId]);

  const selected = syncable.find((row) => String(row.id) === String(storeId));
  const isGokwik = selected && selected.platform === 'GOKWIK';
  const buttonLabel = isGokwik ? 'Sync orders from GoKwik' : 'Sync orders from Shopify';

  const run = async () => {
    if (!storeId) return;
    setBusy(true);
    setSyncOut({
      ok: true,
      started: true,
      status: 'running',
      message: 'Starting order sync…',
    });
    try {
      const data = await runStoreOrderSync(storeId, hours, { onProgress: setSyncOut });
      setSyncOut(data);
      if (data && data.ok !== false && data.status !== 'running' && typeof onSynced === 'function') {
        onSynced(data);
      }
    } catch (err) {
      const data = err && err.data;
      setSyncOut({
        ok: false,
        error: String(err.message || err),
        detail: data && (data.detail || data.message),
        ...((data && typeof data === 'object') ? data : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  if (stores.loading && !rows.length) {
    return (
      <div className="uni-note" style={{ marginBottom: 16 }}>
        Loading stores…
      </div>
    );
  }

  if (!syncable.length) {
    return (
      <div className="uni-note" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px' }}>Sync orders</h3>
        <p className="uni-num--muted" style={{ margin: 0 }}>
          Connect a Shopify store with an Admin token, or a GoKwik store with App ID + App Secret,
          on <Link to="/signalbridge/stores">Stores</Link>. Then you can pull missed orders into this log.
        </p>
      </div>
    );
  }

  return (
    <div className="uni-note" style={{ marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 8px' }}>Sync orders into this log</h3>
      <p className="uni-num--muted" style={{ margin: '0 0 12px' }}>
        Pull Shopify or GoKwik orders from the lookback window. New purchases appear in the table
        below. Already-stored orders are not listed as new — search by order number (for example #3210).
        Long pulls run in the background so the page does not time out — leave this tab open until it finishes.
      </p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <EasyField label="Store">
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            {syncable.map((row) => (
              <option key={row.id} value={row.id}>
                {row.store_domain} ({row.platform === 'GOKWIK' ? 'GoKwik' : 'Shopify'})
              </option>
            ))}
          </select>
        </EasyField>
        <OrderSyncHoursField value={hours} onChange={setHours} />
        <UniActionButton hero primary disabled={busy || !storeId} onClick={run}>
          {busy ? 'Syncing…' : buttonLabel}
        </UniActionButton>
      </div>
      <OrderSyncStatus data={syncOut} />
    </div>
  );
}

export function EasyError({ errors }) {
  if (!errors) return null;
  const text = typeof errors === 'string'
    ? errors
    : Object.values(errors).filter(Boolean).map(String).join(' · ');
  if (!text) return null;
  return <div className="ty-error" style={{ marginBottom: 12 }}>{text}</div>;
}

export function WizardSteps({ steps, current }) {
  return (
    <ol className="sb-steps">
      {(steps || []).map((s) => {
        const on = s.n === current;
        const done = s.n < current;
        return (
          <li
            key={s.n}
            className={`sb-step${on ? ' sb-step--on' : ''}${done ? ' sb-step--done' : ''}`}
          >
            <span className="sb-step__n">{done ? '✓' : s.n}</span>
            <span>{s.title}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function ChannelBadge({ on, label }) {
  return (
    <span className={`sb-channel ${on ? 'sb-channel--on' : 'sb-channel--off'}`}>
      <span className="sb-channel__dot" aria-hidden="true" />
      {label}{on ? '' : ' — add'}
    </span>
  );
}

export function SetupCard({ n, title, sub, to, done, next, cta }) {
  return (
    <Link
      to={to}
      className={`sb-setup-card${done ? ' sb-setup-card--done' : ''}${next && !done ? ' sb-setup-card--next' : ''}`}
    >
      <span className="sb-setup-card__n">{done ? '✓' : n}</span>
      <h3 className="sb-setup-card__title">{title}</h3>
      <p className="sb-setup-card__sub">{sub}</p>
      <span className="sb-setup-card__cta">{done ? 'View' : cta}</span>
    </Link>
  );
}

/**
 * Fast testing panel kept *aside* from pixel create/edit.
 *
 * The Test Events code is typed here and sent with the request. It is
 * never PATCHed onto PixelAccount, so operators can try CAPI / Graph
 * after saving without opening Edit on the main pixel fields.
 */
export function PixelTestAside({
  pixelPk,
  wizardAct,
  busy = false,
  result,
  canAttempt = true,
  blockedReason = '',
  initialCode = '',
}) {
  const [code, setCode] = React.useState(initialCode || '');
  const [eventKind, setEventKind] = React.useState('PageView');
  const [customName, setCustomName] = React.useState('');
  const [localBusy, setLocalBusy] = React.useState('');
  const [out, setOut] = React.useState(null);
  React.useEffect(() => {
    if (initialCode) setCode(initialCode);
  }, [pixelPk, initialCode]);
  const working = busy || !!localBusy;
  const shown = out || result;
  const testBody = () => ({
    test_event_code: code,
    event_type: eventKind,
    custom_event_name: eventKind === 'custom' ? customName : '',
  });

  const run = async (mode) => {
    setLocalBusy(mode);
    setOut(null);
    try {
      if (pixelPk) {
        const data = await postJSON(`${SB_API}/pixels/${pixelPk}/test-connection/`, {
          mode,
          ...testBody(),
        });
        setOut(data);
      } else if (wizardAct) {
        const action = mode === 'pixel' ? 'check_token' : 'verify';
        await wizardAct(action, testBody());
      } else {
        setOut({ error: 'Save the pixel first, then test from this panel.' });
      }
    } catch (err) {
      const detail = err && err.data && (err.data.detail || err.data.error);
      setOut({ error: detail || String(err.message || err) });
    } finally {
      setLocalBusy('');
    }
  };

  return (
    <aside className="sb-test-aside" aria-label="Test CAPI or pixel">
      <h3 className="sb-test-aside__title">Test CAPI / pixel</h3>
      <p className="sb-test-aside__lead">
        Paste a Test Events code, pick the event to send, then send. This does not edit the pixel’s name, ID, or token.
        {' '}
        <Link to="/signalbridge/payloads">Open saved Meta payloads</Link>
      </p>
      <label className="sb-test-aside__label">
        Event to send
        <select value={eventKind} onChange={(e) => setEventKind(e.target.value)}>
          <option value="PageView">PageView</option>
          <option value="AddToCart">AddToCart</option>
          <option value="Purchase">Purchase</option>
          <option value="custom">Custom</option>
        </select>
      </label>
      {eventKind === 'custom' ? (
        <label className="sb-test-aside__label">
          Custom event name
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="CompleteRegistration"
            autoComplete="off"
          />
        </label>
      ) : null}
      <label className="sb-test-aside__label">
        Test event code
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="TEST12345"
          autoComplete="off"
        />
      </label>
      {!canAttempt && blockedReason ? (
        <p className="uni-num--muted">{blockedReason}</p>
      ) : null}
      <div className="sb-test-aside__actions">
        <button
          type="button"
          className="ty-btn ty-btn--secondary"
          disabled={working || !canAttempt}
          onClick={() => run('pixel')}
        >
          {localBusy === 'pixel' ? 'Checking…' : 'Test pixel'}
        </button>
        <button
          type="button"
          className="ty-btn"
          disabled={working || !canAttempt}
          onClick={() => run('capi')}
        >
          {localBusy === 'capi' ? 'Sending…' : 'Send CAPI test'}
        </button>
      </div>
      {shown && (
        shown.error
          ? <p className="ty-error">{String(shown.error)}</p>
          : <TechDetails title="What Meta returned" data={shown} />
      )}
    </aside>
  );
}

export function ExistingPicker({
  items,
  value,
  onSelect,
  onCreateNew,
  label = 'Previously saved',
  hint = 'Select details you already entered to edit them, or start with a blank form.',
  createLabel = 'Enter new details',
}) {
  if (!items || !items.length) return null;
  return (
    <div className="sb-existing">
      <EasyField label={label} hint={hint}>
        <select
          value={value || ''}
          onChange={(e) => {
            const next = e.target.value;
            if (!next) {
              if (onCreateNew) onCreateNew();
            } else if (onSelect) {
              onSelect(next);
            }
          }}
        >
          <option value="">{createLabel}</option>
          {items.map((it) => (
            <option key={it.id} value={it.id}>{it.label || it.name || it.id}</option>
          ))}
        </select>
      </EasyField>
    </div>
  );
}

export function ForceNextButton({ onClick, disabled, last }) {
  return (
    <button type="button" className="sb-force-next" disabled={disabled} onClick={onClick}>
      {last ? 'Force finish' : 'Force next screen'}
      {' '}
      <span className="sb-force-next__warn">(Not recommended)</span>
    </button>
  );
}

export function OnboardChecks({ checks }) {
  if (!checks || !checks.length) return null;
  return (
    <ul className="sb-onboard-checks">
      {checks.map((c) => (
        <li key={c.key} className={c.ok ? 'sb-onboard-checks__ok' : 'sb-onboard-checks__fail'}>
          <strong>{c.ok ? 'Passed' : 'Needs work'}:</strong> {c.label}
          {!c.ok && c.fix ? <span className="uni-num--muted"> {c.fix}</span> : null}
        </li>
      ))}
    </ul>
  );
}

export function useWizard(slug, opts = {}) {
  const [searchParams] = useSearchParams();
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const onFinishedRef = React.useRef(opts.onFinished);
  onFinishedRef.current = opts.onFinished;
  const finishedRef = React.useRef(false);

  const qs = React.useMemo(() => {
    const p = new URLSearchParams();
    const pixel = opts.pixel ?? searchParams.get('pixel');
    const store = opts.store ?? searchParams.get('store');
    const destination = opts.destination ?? searchParams.get('destination');
    const merge = opts.merge ?? searchParams.get('merge');
    const isNew = opts.new ?? searchParams.get('new');
    if (pixel) p.set('pixel', pixel);
    if (store) p.set('store', store);
    if (destination) p.set('destination', destination);
    if (merge) p.set('merge', merge);
    if (isNew && isNew !== '0' && isNew !== false) p.set('new', '1');
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [opts.pixel, opts.store, opts.destination, opts.merge, opts.new, searchParams]);

  const load = React.useCallback(async () => {
    const data = await getJSON(`${SB_API}/wizards/${slug}/${qs}`);
    setState(data);
  }, [slug, qs]);

  React.useEffect(() => {
    load().catch((e) => setState({ errors: { load: String(e.message || e) } }));
  }, [load]);

  React.useEffect(() => {
    if (state?.status === 'COMPLETED' && onFinishedRef.current && !finishedRef.current) {
      finishedRef.current = true;
      onFinishedRef.current(state);
    }
    if (state?.status === 'IN_PROGRESS') finishedRef.current = false;
  }, [state?.status]);

  const data = state?.collected_data || {};
  const setField = (k, v) => setState((s) => ({ ...s, collected_data: { ...(s.collected_data || {}), [k]: v } }));
  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      const payload = await postJSON(`${SB_API}/wizards/${slug}/`, {
        action, id: state?.id, data: { ...data, ...extra },
      });
      setState(payload);
      return payload;
    } catch (err) {
      setState((s) => ({ ...s, errors: { submit: String(err.message || err) } }));
      if (action === 'force_finish') throw err;
    } finally {
      setBusy(false);
    }
  };
  if (opts.forceRef) {
    opts.forceRef.current = () => act('force_finish');
  }
  return { state, busy, data, setField, act, setState, reload: load };
}

export function WizardShell({
  title, subtitle, helpPage, state, busy, act, aside, children, embedded, home = HOME,
}) {
  const step = state?.current_step || 1;
  const last = (state?.steps || []).length || 1;
  const done = state?.status === 'COMPLETED';
  const inner = !state ? (
    <div className="uni-state">Loading…</div>
  ) : (
    <div className={aside ? 'sb-wizard-with-aside' : undefined}>
      <div>
        {embedded ? <h3 className="sb-wizard-embed__title">{title}</h3> : null}
        {embedded && subtitle ? <p className="uni-num--muted">{subtitle}</p> : null}
        <WizardSteps steps={state.steps} current={step} />
        <EasyError errors={state.errors} />
        {state.detail && <p className="uni-num--muted">{state.detail}</p>}
        {done && (
          <p className="sb-wizard-saved">
            Saved{state.created?.name ? `: ${state.created.name}` : ''}.
            {embedded ? ' Onboarding will re-check this step and continue if it passed.' : ''}
          </p>
        )}
        {children}
        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {!done && (
            <>
              <UniActionButton hero disabled={busy || step <= 1} onClick={() => act('back')}>Back</UniActionButton>
              {step < last && (
                <UniActionButton hero primary disabled={busy} onClick={() => act('next')}>
                  {busy ? 'Checking…' : 'Continue'}
                </UniActionButton>
              )}
              {step >= last && (
                <UniActionButton hero primary disabled={busy} onClick={() => act('complete')}>
                  {busy ? 'Saving…' : 'Finish'}
                </UniActionButton>
              )}
              <ForceNextButton last={step >= last} disabled={busy} onClick={() => act('force_next')} />
            </>
          )}
        </div>
      </div>
      {aside}
    </div>
  );
  if (embedded) {
    return <div className="sb-wizard-embed">{inner}</div>;
  }
  if (!state) {
    return <UniPageShell theme={THEME} api={SB_API} home={home} icon="1" title={title}><div className="uni-state">Loading…</div></UniPageShell>;
  }
  return (
    <UniPageShell
      theme={THEME}
      api={SB_API}
      home={home}
      icon="1"
      title={title}
      subtitle={subtitle}
      helpPage={helpPage}
      helpApi={SB_API}
      helpOpen={false}
      actions={<UniActionButton hero to={home}>← Home</UniActionButton>}
    >
      {inner}
    </UniPageShell>
  );
}

function customAppCredentialError(clientId, clientSecret) {
  const id = (clientId || '').trim();
  const secret = (clientSecret || '').trim();
  if (id.toLowerCase().startsWith('shpss_')) {
    return 'That is the Client secret (shpss_…). Put it in Client secret. Client ID is the other value on the same Settings page.';
  }
  if (id.toLowerCase().startsWith('shpat_')) {
    return 'New store apps do not use a static Admin token. Create the app in this store’s Dev Dashboard and paste Client ID + Client secret.';
  }
  if (secret.toLowerCase().startsWith('shpat_')) {
    return 'Client secret is shpss_… (or the Client secret shown in Dev Dashboard Settings), not an Admin token.';
  }
  return '';
}

export function isShopifyAdminEmbed() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1' || params.has('id_token') || params.has('host')) {
      return true;
    }
    return window.self !== window.top;
  } catch (err) {
    return true;
  }
}

export function isShopifyListingAppHost() {
  const host = (window.location.hostname || '').toLowerCase();
  return host === 'connect.tasky.do' || host.startsWith('connect.');
}

export function partnersOauthBlockedInEmbed() {
  return isShopifyAdminEmbed() && !isShopifyListingAppHost();
}

export function openShopifyInstallUrl(url) {
  if (!url) return;
  try {
    (window.top || window).location.assign(url);
  } catch (err) {
    window.location.assign(url);
  }
}

export function ShopifyAppInstallCard() {
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const blockedInCustomEmbed = partnersOauthBlockedInEmbed();

  const connect = async () => {
    setMessage('');
    if (blockedInCustomEmbed) {
      setMessage(
        'Partners OAuth cannot run inside this custom-app embed — Shopify blocks that consent page in the iframe, so Admin goes blank. Use Client ID and Client secret below instead.',
      );
      return;
    }
    setBusy(true);
    try {
      const data = await getJSON(`${SB_API}/shopify-app/install-url/`);
      if (!data.configured || !data.install_url) {
        setMessage(data.detail || 'Shopify Partner install is not configured on this organisation.');
        return;
      }
      openShopifyInstallUrl(data.install_url);
    } catch (err) {
      const detail = err && err.data && (err.data.detail || err.data.error);
      setMessage(detail || String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sb-connect-panel">
      <h2>Install from Shopify (Partners / App Store)</h2>
      <p className="uni-num--muted">
        {blockedInCustomEmbed
          ? 'This Admin screen is already the store-owned custom app. Skip Partners / App Store install here and use Client ID + Client secret in the card below.'
          : 'Use this for the public Taskydo-SignalBridge Connect app. Shopify runs OAuth and issues the token — you do not paste Client ID or secret.'}
      </p>
      {blockedInCustomEmbed ? null : (
        <div>
          <UniActionButton hero primary onClick={connect} disabled={busy}>
            {busy ? 'Opening Shopify…' : 'Install SignalBridge on Shopify'}
          </UniActionButton>
        </div>
      )}
      {message ? <p className="sb-connect-error">{message}</p> : null}
    </div>
  );
}

export function CustomShopifyAppCard({ onConnected }) {
  const [shop, setShop] = React.useState('');
  const [alias, setAlias] = React.useState('');
  const [publicDomain, setPublicDomain] = React.useState('');
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [ok, setOk] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    getJSON(`${SB_API}/shopify-app/install-url/`)
      .then((data) => {
        if (cancelled || !data) return;
        if (data.default_shop) setShop((current) => current || data.default_shop);
        if (data.default_shop_alias) setAlias((current) => current || data.default_shop_alias);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const connect = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setMessage('');
    setOk(false);
    setResult(null);
    const credentialError = customAppCredentialError(clientId, clientSecret);
    if (credentialError) {
      setMessage(credentialError);
      return;
    }
    setBusy(true);
    try {
      const data = await postJSON(`${SB_API}/shopify-app/custom-connect/`, {
        shop,
        alias,
        public_domain: publicDomain,
        client_id: clientId,
        client_secret: clientSecret,
      });
      setResult(data);
      setOk(!!data.ok);
      setMessage(data.detail || (data.ok ? 'Store connected with the custom app.' : 'Could not connect that custom app.'));
      if (data.ok) {
        setClientId('');
        setClientSecret('');
        if (onConnected) onConnected(data);
      }
    } catch (err) {
      const detail = err && err.data && (err.data.detail || err.data.error);
      setMessage(detail || String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="sb-connect-panel" onSubmit={connect}>
      <h2>Connect with a custom app (this store only)</h2>
      <p className="uni-num--muted">
        Use this when the shop cannot install the Partners / App Store app
        (different Shopify organization, or App Store review is too slow).
        Create the app <strong>inside that store’s Admin</strong> so it is in
        the same organization. SignalBridge then uses Client ID + Client secret
        (client credentials). The listing app stays unchanged.
      </p>
      <ol className="uni-num--muted">
        <li>That store’s Shopify Admin → Settings → Apps → Develop apps → Build apps in Dev Dashboard.</li>
        <li>Create app → Start from Dev Dashboard.</li>
        <li>Versions: app URL <code>https://shopify.dev/apps/default-app-home</code>, webhooks API latest, scopes <code>read_orders,read_checkouts,read_customers</code>. Release.</li>
        <li>Home → Install app → this same store.</li>
        <li>Settings → copy Client ID and Client secret into the fields below.</li>
      </ol>
      <EasyField label="myshopify domain" hint="Canonical host. Example: a00cny-n6.myshopify.com">
        <input
          placeholder="a00cny-n6.myshopify.com"
          value={shop}
          autoComplete="off"
          onChange={(e) => setShop(e.target.value)}
        />
      </EasyField>
      <EasyField label="Alias myshopify domain (optional)" hint="If the shop also has janayush.myshopify.com, paste it here so both resolve.">
        <input
          placeholder="janayush.myshopify.com"
          value={alias}
          autoComplete="off"
          onChange={(e) => setAlias(e.target.value)}
        />
      </EasyField>
      <EasyField label="Customer-facing URL" hint="https://janayush.com — used only when an event has no landing page.">
        <input
          placeholder="https://janayush.com"
          value={publicDomain}
          autoComplete="off"
          onChange={(e) => setPublicDomain(e.target.value)}
        />
      </EasyField>
      <EasyField label="Client ID" hint="From this store’s Dev Dashboard app → Settings. Not the App Store listing Client ID.">
        <input
          autoComplete="off"
          placeholder="Client ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
        />
      </EasyField>
      <EasyField label="Client secret" hint="From the same Settings page. Used to request tokens and verify webhooks.">
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Client secret"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
        />
      </EasyField>
      <div>
        <UniActionButton hero primary onClick={connect} disabled={busy || !shop || !clientId || !clientSecret}>
          {busy ? 'Connecting…' : 'Connect custom app'}
        </UniActionButton>
      </div>
      {message ? (
        <p className={ok ? 'uni-num--muted' : 'sb-connect-error'}>{message}</p>
      ) : null}
      <TechDetails title="Shopify custom app result" data={result} />
    </form>
  );
}

export function ShopifyConnectCodeCard({ autoRedeem = false, onConnected, later = false }) {
  const search = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search);
    } catch {
      return new URLSearchParams();
    }
  }, []);
  const [connectCode, setConnectCode] = React.useState(
    () => search.get('connect_code') || search.get('code') || '',
  );
  const [preview, setPreview] = React.useState(null);
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const autoTried = React.useRef(false);

  React.useEffect(() => {
    if (!connectCode) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    getJSON(`${SB_API}/shopify-app/redeem-connect-code/?code=${encodeURIComponent(connectCode)}`)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview({ ok: false, detail: String(err.message || err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [connectCode]);

  const redeem = React.useCallback(async () => {
    setMessage('');
    setBusy(true);
    try {
      const data = await postJSON(`${SB_API}/shopify-app/redeem-connect-code/`, {
        code: connectCode,
      });
      setPreview(data);
      setMessage(data.detail || (data.ok ? 'Shop connected.' : 'Could not connect that code.'));
      if (data.ok && onConnected) onConnected(data);
    } catch (err) {
      setMessage(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }, [connectCode, onConnected]);

  React.useEffect(() => {
    if (!autoRedeem || autoTried.current) return;
    if (!connectCode || !preview || !preview.ok || preview.redeemed) return;
    autoTried.current = true;
    redeem();
  }, [autoRedeem, connectCode, preview, redeem]);

  const inner = (
    <>
      <h2 style={{ margin: '0 0 8px' }}>
        {later ? 'App Store listing (later)' : 'Connect your Shopify store'}
      </h2>
      <p className="uni-num--muted">
        {later
          ? 'When the Partners app is approved, open Taskydo-SignalBridge Connect in Shopify Admin, generate a connect code, and paste it here.'
          : 'In Shopify Admin open Taskydo-SignalBridge Connect, generate a connect code, then paste it here. That attaches this shop to this workspace.'}
      </p>
      <EasyField label="Connect code" hint="ABCD-EFGH from the Shopify app">
        <input
          placeholder="ABCD-EFGH"
          value={connectCode}
          onChange={(e) => setConnectCode(e.target.value)}
        />
      </EasyField>
      {preview && preview.shop ? (
        <p className="uni-num--muted">
          This code is for <strong>{preview.shop}</strong>
          {preview.redeemed ? ' (already used).' : '.'}
        </p>
      ) : null}
      <UniActionButton hero primary onClick={redeem} disabled={busy || !connectCode}>
        {busy ? 'Connecting…' : 'Connect this shop'}
      </UniActionButton>
      {message ? <p className="uni-num--muted">{message}</p> : null}
    </>
  );
  if (later) {
    return (
      <details className="sb-connect-later">
        <summary>App Store listing — connect with the Partners app later</summary>
        <div style={{ marginTop: 12 }}>{inner}</div>
      </details>
    );
  }
  return (
    <div className="sb-connect-panel">
      {inner}
    </div>
  );
}
